import React, { FunctionComponent } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { formatLabelWithEllipsis, ReportH5 } from "../../../../Common/";
import { Sidebar, SidebarSupp } from "./RetailMatchTemplateStyles";
import DigitalShareTemplate from "../../../../Common/DigitalShare/Templates/DigitalShareTemplate";
import { retailMatchReportDataSelector } from "../../../../../Reducers/Selectors/RetailMatchSelectors";
import {
    RetailMatchIntroBrandingContainer,
    RetailMatchShareIntroBrandLogo,
    RetailMatchShareIntroBrandLogoWrapper,
} from "../../Styled/RetailMatchIntroStyles";
import { RetailMatchKeyMetricsSharing } from "../../Components/RetailMatchKeyMetricsSharing";
import {
    brandOverviewRecord,
    getBenchmarkAddressOverride,
} from "../../../../../Util/RetailMatch/RetailMatchUtil";
import { IStoreScoreRecord } from "../../../../../Models/IRetailMatch";
import {
    MainContent,
    MainContentChild,
    MainContentText,
    MainContentTitle,
    Wrapper,
} from "../../../../Common/DigitalShare/Templates/TemplateStyles";
import {
    digitalShareElementText,
    mapSnapshots,
} from "../../../../Common/DigitalShare/DigitalShareGenerator";
import { DigitalShareItemType } from "../../../../../Enums/DigitalShareItemType";
import { ReportMapWrapper } from "../../../../Common/Map/Styles/MapStyles";
import { DigitalShareAppType } from "../../../../../Enums/DigitalShareAppType";
export interface IRetailMatchDigitalShareTemplate {
  children: React.ReactNode;
  captionAboveTitle?: string;
  sectionTitle: string;
  disclaimer: string;
  itemType: string;
  hideItemTypeTitle?: boolean;
  hideItemText?: boolean;
}

const RetailMatchDigitalShareTemplate: FunctionComponent<IRetailMatchDigitalShareTemplate>
  = (props: IRetailMatchDigitalShareTemplate) => {
      const { jobResult, benchmarkRecord, selectedBrand } = useSelector(
          retailMatchReportDataSelector,
          shallowEqual
      );

      const benchmarkAddressOverride: IStoreScoreRecord
      = getBenchmarkAddressOverride(jobResult, benchmarkRecord);

      const brand = selectedBrand || jobResult?.job?.brands[0];
      const overviewRecord = brandOverviewRecord(
          brand?.brandId,
          jobResult?.chartData?.overview
      );

      const isSiteDetails = (): boolean => {
          return (
              props.itemType == DigitalShareItemType.KeyMetrics
        || props.itemType == DigitalShareItemType.BrandComparison
          );
      };

      return (
          <DigitalShareTemplate
              appName={DigitalShareAppType.RetailMatch}
              captionAboveTitle={props.captionAboveTitle}
              sectionTitle={props.sectionTitle}
          >
              <Wrapper data-testid="retailmatch-share-template">
                  <Sidebar>
                      {props.itemType == DigitalShareItemType.BrandComparison && (
                          <div style={{ border: "solid 1px #e4e4e4", marginBottom: 14 }}>
                              <ReportMapWrapper
                                  data-testid="retailmatch-digishare-template-map"
                                  style={{ width: 310, height: 227 }}
                              >
                                  {mapSnapshots.get("retail-match-overview-map")}
                              </ReportMapWrapper>
                              <div
                                  style={{
                                      padding: "8px 7px 9px 8px",
                                      display: "flex",
                                      flexDirection: "column",
                                  }}
                              >
                                  <ReportH5
                                      style={{ margin: 0, padding: 0, textAlign: "center" }}
                                  >
                    Know the potential for
                                      <br /> any brand at any property.
                                  </ReportH5>
                                  <p
                                      style={{
                                          fontSize: 12,
                                          textAlign: "center",
                                          lineHeight: "33px",
                                          margin: 0,
                                      }}
                                  >
                    Visit{" "}
                                      <a href="https://buxtonco.com/match">buxtonco.com/match</a>{" "}
                    for more information
                                  </p>
                              </div>
                          </div>
                      )}
                      {props.itemType !== DigitalShareItemType.BrandComparison && (
                          <div
                              style={{
                                  borderRadius: "3px",
                                  border: "solid 1px #e7e8ed",
                                  backgroundColor: "#ffffff",
                                  marginBottom: "14px",
                              }}
                          >
                              <RetailMatchIntroBrandingContainer
                                  style={{ height: "227px", marginBottom: "0px" }}
                              >
                                  <RetailMatchShareIntroBrandLogoWrapper>
                                      <RetailMatchShareIntroBrandLogo
                                          background={brand?.logoPath}
                                      />
                                  </RetailMatchShareIntroBrandLogoWrapper>
                                  <ReportH5>
                    Know the potential for
                                      <br /> any brand at any property.
                                  </ReportH5>
                              </RetailMatchIntroBrandingContainer>
                              <div
                                  style={{
                                      fontSize: 12,
                                      textAlign: "center",
                                      lineHeight: "33px",
                                  }}
                              >
                  Visit{" "}
                                  <a href="https://buxtonco.com/match">buxtonco.com/match</a>{" "}
                  for more information
                              </div>
                          </div>
                      )}
                      <SidebarSupp isSiteDetails={isSiteDetails()}>
                          {(props.itemType == DigitalShareItemType.KeyMetrics
                || props.itemType == DigitalShareItemType.BrandComparison)
              && jobResult.job.siteDetails?.length > 0 ? (
                                  <>
                                      <span
                                          data-testid="site-details-sharing"
                                          style={{ fontSize: 13 }}
                                      >
                    SITE DETAILS
                                      </span>
                                      <div
                                          style={{
                                              wordBreak: "break-word",
                                              fontSize: 11,
                                              lineHeight: "16.5px",
                                          }}
                                      >
                                          {formatLabelWithEllipsis(jobResult.job.siteDetails, 751)}
                                      </div>
                                  </>
                              ) : null}
                      </SidebarSupp>
                  </Sidebar>
                  <MainContent>
                      {!props.hideItemTypeTitle && (
                          <MainContentTitle data-testid="retail-match-template-title">
                              {props.itemType}
                          </MainContentTitle>
                      )}
                      {!props.hideItemText && (
                          <MainContentText data-testid="retail-match-template-title-text">
                              {digitalShareElementText.get(props.itemType)}
                          </MainContentText>
                      )}
                      <MainContentChild>{props.children}</MainContentChild>
                  </MainContent>
              </Wrapper>
          </DigitalShareTemplate>
      );
  };

export default React.memo(RetailMatchDigitalShareTemplate);
